import {
  Job,
  StateMachineState,
  JobOptions,
  JobQueueJobs,
  QUEUE_CALL_TIMEOUT,
} from './types'
import { Tab, TabEventMap } from 'tab-election'
import { log } from '../logger'
import { JobQueueDatabase } from './JobQueueDatabase'
import { isJobFinished } from './helpers'
import { delay } from '@pogokid/util'

export interface JobQueueClientOptions {
  namespace?: string
  init?: () => Promise<void>
  /** Defaults to {@link QUEUE_CALL_TIMEOUT}. */
  callTimeout?: number
}

/** Merge two partial states, with `override` winning. */
function mergeState<State extends StateMachineState>(
  base: Partial<State> | null,
  override: Partial<State> | null
): Partial<State> {
  return { ...base, ...override } as Partial<State>
}

export type InferJobQueueClientState<QC> =
  QC extends JobQueueClient<infer S, JobQueueJobs> ? S : never

export type InferJobQueueClientJobs<QC> =
  QC extends JobQueueClient<StateMachineState, infer J>
    ? J
    : never

export class JobQueueClient<
  State extends StateMachineState,
  Jobs extends JobQueueJobs
> {
  private tab: Tab
  private database: JobQueueDatabase

  /**
   * State that has not reached a hub yet.
   *
   * Most pages never start a hub: the public pages, the sign-out
   * screen, and the whole sign-in round trip all mount the auth
   * provider, and it pushes the session into the queue as soon as it
   * mounts. tab-election holds a call until a leader exists and then
   * rejects it with "Call timed out", which throws the state away and
   * reports an unhandled error.
   *
   * So we hold the newest state here instead and send it when a hub
   * takes leadership.
   */
  private pendingState: Partial<State> | null = null
  private waitingForLeader = false
  /** Tail of the flush chain, so only one runs at a time. */
  private flushing: Promise<void> = Promise.resolve()

  constructor(options: JobQueueClientOptions = {}) {
    this.tab = new Tab(options.namespace || 'web-queue', {
      callTimeout: options.callTimeout ?? QUEUE_CALL_TIMEOUT,
    })
    this.database = new JobQueueDatabase()
    this.database.init().catch((error: Error) => {
      log.error(
        'web-queue:database error initialising db',
        error
      )
    })
  }

  addEventListener<K extends keyof TabEventMap>(
    type: K,
    listener: (
      this: BroadcastChannel,
      ev: TabEventMap[K]
    ) => unknown,
    options?: boolean | AddEventListenerOptions
  ): void
  addEventListener(type: string, listener: EventListener) {
    return this.tab.addEventListener(type, listener)
  }

  removeEventListener<K extends keyof TabEventMap>(
    type: K,
    listener: (
      this: BroadcastChannel,
      ev: TabEventMap[K]
    ) => unknown,
    options?: boolean | EventListenerOptions
  ): void
  removeEventListener(type: string, listener: EventListener) {
    return this.tab.removeEventListener(type, listener)
  }

  private callVoid(name: string, ...rest: unknown[]) {
    this.tab.call(name, ...rest).catch((error) => {
      log.error('web-queue:callError', error)
    })
  }

  startProcessing(initialState?: Partial<State>): void {
    if (initialState) {
      this.setState(initialState).then(() => {
        this.callVoid('startProcessing')
      })
    } else {
      this.callVoid('startProcessing')
    }
  }

  async enqueue<K extends keyof Jobs, D = Jobs[K]>(
    type: K,
    data?: D,
    options: JobOptions = {}
  ): Promise<string> {
    return this.tab.call('enqueue', type, data, options)
  }

  enqueueSync<K extends keyof Jobs>(
    type: K,
    data: Jobs[K],
    options: JobOptions = {}
  ): void {
    this.enqueue(type, data, options)
      .then((jobId) => {
        log.debug('web-queue:enqueue', 'queued job', jobId)
      })
      .catch((error) => {
        log.error('web-queue:enqueue', error)
      })
  }

  async setState(state: Partial<State>): Promise<void> {
    log.debug('web-queue:setState', Object.keys(state))
    this.pendingState = mergeState(this.pendingState, state)
    await this.flushState()
  }

  /**
   * Send the held state, one flush at a time.
   *
   * Two calls in flight together can be holding different snapshots
   * of the state, and then whichever fails last puts its snapshot
   * back — which may be the older of the two, losing the newer.
   * Running them in turn means a failed call can only ever put back
   * state that is older than what is held now, so the merge below
   * stays correct.
   */
  private flushState(): Promise<void> {
    this.flushing = this.flushing
      .then(() => this.sendPendingState())
      .catch((error) => {
        // Nothing above is expected to throw, but if it does the
        // chain must not be left rejected: every later update would
        // be dropped with it, and each one would surface as an
        // unhandled error. The state stays held for the next try.
        log.error('web-queue:setState', error)
      })
    return this.flushing
  }

  private async sendPendingState(): Promise<void> {
    if (!this.pendingState) return

    // Without an elected hub the call would sit in tab-election's
    // queue and then reject, so keep hold of the state instead
    if (!(await this.tab.hasLeader())) {
      this.flushStateWhenLeaderElected()
      return
    }

    // Read after the await so a state set while we were checking is
    // not dropped
    const state = this.pendingState
    if (!state) return
    this.pendingState = null

    try {
      await this.tab.call('updateState', state)
    } catch (error) {
      // The hub went away mid-call, or took longer than the call
      // timeout to start. Keep the state for the next hub, letting
      // anything set while the call was in flight win.
      this.pendingState = mergeState(state, this.pendingState)
      log.error('web-queue:setState', error)
      this.flushStateWhenLeaderElected()
    }
  }

  /**
   * A hub broadcasts its state to every tab the moment it wins the
   * election, so the next state event is the signal to try again.
   */
  private flushStateWhenLeaderElected(): void {
    if (this.waitingForLeader) return
    this.waitingForLeader = true
    const onState = () => {
      this.tab.removeEventListener('state', onState)
      this.waitingForLeader = false
      void this.flushState()
    }
    this.tab.addEventListener('state', onState)
  }

  getState(): State {
    return this.tab.getState() as State
  }

  async cancelJob(jobId: string): Promise<boolean> {
    return this.tab.call('cancelJob', jobId)
  }

  async destroy(): Promise<void> {
    // Close the BroadcastChannel so the tab stops
    // listening for hub messages
    this.tab.close()
  }

  async clear(): Promise<void> {
    // await this.tab.call('stopProcessing')
    await this.database.clear()
  }

  getJob(jobId: string): Promise<Job | undefined> {
    return this.database.getJob(jobId)
  }

  async waitForJob<R>(
    jobId: string,
    options: { timeout?: number } = {}
  ) {
    let job: Job<unknown, R> | undefined | null = null
    const timeout = Date.now() + (options.timeout ?? 6_000)
    while (!job) {
      if (Date.now() > timeout) {
        throw new Error(`web-queue:waitForJob timeout ${jobId}`)
      }
      job = (await this.getJob(jobId)) as
        | Job<unknown, R>
        | undefined
      if (job && isJobFinished(job)) {
        return job
      } else {
        job = null
        await delay(100)
      }
    }
  }
}
